import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Stack,
  Container,
  FormControlLabel,
  Switch,
  Divider,
  InputAdornment,
} from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import HistoryIcon from '@mui/icons-material/History';

const PRIMARY = '#6366f1';

function WorkoutLogPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    exercise: '',
    load_kg: '',
    reps: '',
    sets: '',
    notes: '',
    is_bodyweight: false,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [bodyweight, setBodyweight] = useState(() => {
    return localStorage.getItem('userBodyweight') || '70';
  });

  const commonExercises = [
    'Barbell Bench Press (Flat)',
    'Barbell Bench Press (45° Incline)',
    'Barbell Squat',
    'Barbell Deadlift',
    'Barbell Overhead Press',
    'Barbell Row',
    'Dumbbell Bench Press',
    'Dumbbell Shoulder Press',
    'Pull-ups (Bodyweight)',
    'Chin-ups (Bodyweight)',
    'Dips (Bodyweight)',
    'Push-ups (Bodyweight)',
    'Lat Pulldown',
    'Leg Press',
    'Romanian Deadlift',
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'is_bodyweight') {
      setFormData({
        ...formData,
        is_bodyweight: checked,
        load_kg: checked ? bodyweight : '',
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
  };

  const handleBodyweightChange = (e) => {
    const newBodyweight = e.target.value;
    setBodyweight(newBodyweight);
    localStorage.setItem('userBodyweight', newBodyweight);
    if (formData.is_bodyweight) {
      setFormData({ ...formData, load_kg: newBodyweight });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const { is_bodyweight, ...workoutData } = formData;
      await api.post('/api/workouts', workoutData);
      setMessage('Workout logged successfully!');
      setFormData({
        date: formData.date,
        exercise: '',
        load_kg: '',
        reps: '',
        sets: '',
        notes: '',
        is_bodyweight: false,
      });
      window.scrollTo(0, 0);
    } catch (error) {
      setMessage('Error logging workout: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const isError = message.includes('Error');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="sm">
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FitnessCenterIcon sx={{ color: PRIMARY, fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Log Workout
            </Typography>
          </Stack>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate('/workout/history')}
            sx={{
              borderColor: PRIMARY,
              color: PRIMARY,
              '&:hover': { borderColor: PRIMARY, bgcolor: `${PRIMARY}10` },
            }}
          >
            View History
          </Button>
        </Stack>

        {/* Feedback message */}
        {message && (
          <Alert severity={isError ? 'error' : 'success'} sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        {/* Info note */}
        <Alert severity="info" sx={{ mb: 2, borderLeft: `4px solid ${PRIMARY}`, bgcolor: 'rgba(99,102,241,0.1)' }}>
          <strong>Note:</strong> <em>Barbell weight (20kg) is always included in the load.</em>
        </Alert>

        {/* Bodyweight setting */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            border: `2px solid ${PRIMARY}`,
            borderRadius: 2,
            bgcolor: 'rgba(99,102,241,0.1)',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2}>
            <Typography fontWeight={600} color="text.primary" sx={{ whiteSpace: 'nowrap' }}>
              Your Bodyweight:
            </Typography>
            <TextField
              type="number"
              value={bodyweight}
              onChange={handleBodyweightChange}
              inputProps={{ step: 0.5, min: 30, max: 200 }}
              size="small"
              sx={{ width: 120 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">kg</InputAdornment>,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Used for bodyweight exercises
            </Typography>
          </Stack>
        </Paper>

        {/* Form */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            {/* Section: Exercise */}
            <Typography variant="overline" color={PRIMARY} fontWeight={700} letterSpacing={1.2}>
              Exercise
            </Typography>
            <Divider sx={{ mb: 2, mt: 0.5 }} />

            <Stack spacing={2} mb={3}>
              <TextField
                label="Date"
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Exercise"
                name="exercise"
                value={formData.exercise}
                onChange={handleChange}
                placeholder="e.g., Barbell Bench Press (45° Incline)"
                required
                fullWidth
                inputProps={{ list: 'exercise-suggestions' }}
              />
              <datalist id="exercise-suggestions">
                {commonExercises.map((ex) => (
                  <option key={ex} value={ex} />
                ))}
              </datalist>
            </Stack>

            {/* Section: Load */}
            <Typography variant="overline" color={PRIMARY} fontWeight={700} letterSpacing={1.2}>
              Load
            </Typography>
            <Divider sx={{ mb: 2, mt: 0.5 }} />

            <Stack spacing={1} mb={3}>
              <TextField
                label="Load (kg)"
                type="number"
                name="load_kg"
                value={formData.load_kg}
                onChange={handleChange}
                placeholder="e.g., 70"
                inputProps={{ step: 0.5, min: 0 }}
                required
                disabled={formData.is_bodyweight}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    name="is_bodyweight"
                    checked={formData.is_bodyweight}
                    onChange={handleChange}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: PRIMARY,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Use Bodyweight
                  </Typography>
                }
              />
              <Typography variant="caption" color="text.secondary">
                {formData.is_bodyweight
                  ? `Using your bodyweight: ${bodyweight}kg`
                  : 'Total weight including barbell'}
              </Typography>
            </Stack>

            {/* Section: Sets / Reps */}
            <Typography variant="overline" color={PRIMARY} fontWeight={700} letterSpacing={1.2}>
              Sets &amp; Reps
            </Typography>
            <Divider sx={{ mb: 2, mt: 0.5 }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
              <TextField
                label="Reps"
                type="number"
                name="reps"
                value={formData.reps}
                onChange={handleChange}
                placeholder="e.g., 8"
                inputProps={{ min: 1 }}
                required
                fullWidth
              />
              <TextField
                label="Sets"
                type="number"
                name="sets"
                value={formData.sets}
                onChange={handleChange}
                placeholder="e.g., 3"
                inputProps={{ min: 1 }}
                required
                fullWidth
              />
            </Stack>

            {/* Section: Notes */}
            <Typography variant="overline" color={PRIMARY} fontWeight={700} letterSpacing={1.2}>
              Notes
            </Typography>
            <Divider sx={{ mb: 2, mt: 0.5 }} />

            <TextField
              label="Notes (Optional)"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="e.g., Felt strong today, good form"
              multiline
              rows={3}
              fullWidth
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                bgcolor: PRIMARY,
                py: 1.5,
                fontWeight: 700,
                fontSize: '1rem',
                borderRadius: 2,
                '&:hover': { bgcolor: '#4f46e5' },
                '&:disabled': { bgcolor: '#a5b4fc' },
              }}
            >
              {loading ? 'Logging...' : 'Log Workout'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default WorkoutLogPage;
